import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

export const CollapsedToggle = styled.div(({ theme, topOffset, hasToolbar }) => ({
  position: 'fixed',
  right: 16,
  top: hasToolbar ? (topOffset + 64) : (topOffset + 16),
  width: 48,
  height: 48,
  background: theme.codePanel.background,
  border: `1px solid ${theme.codePanel.border}`,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: theme.codePanel.text,
  boxShadow: theme.codePanel.shadow,
  zIndex: 1000
}));

export const Panel = styled.div(({ theme, wide, hasToolbar, topOffset }) => ({
  position: 'fixed',
  right: 0,
  top: hasToolbar ? (56 + topOffset) : topOffset,
  width: wide ? 800 : 600,
  height: `calc(100vh - ${(hasToolbar ? 56 : 0) + topOffset}px)`,
  background: theme.codePanel.background,
  borderLeft: `1px solid ${theme.codePanel.border}`,
  display: 'flex',
  flexDirection: 'column',
  zIndex: 1000,
  boxShadow: theme.codePanel.shadow,
  transition: 'width .25s ease',
  resize: 'horizontal',
  overflow: 'hidden',
  minWidth: 400,
  maxWidth: '80vw'
}));

export const PanelHeader = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 10px',
  gap: 6,
  borderBottom: `1px solid ${theme.codePanel.border}`
}));

export const Title = styled.span(({ theme }) => ({
  flex: 1,
  fontSize: 13,
  color: theme.codePanel.text,
  fontWeight: 500,
  letterSpacing: '.5px'
}));

export const IconButton = styled.button(({ theme }) => ({
  background: 'transparent',
  border: `1px solid ${theme.codePanel.border}`,
  color: theme.codePanel.text,
  width: 34,
  height: 34,
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.name === 'light' ? '#f1f5f9' : '#374151'
  },
  '&:disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none'
  }
}));

export const EditorWrapper = styled.div({ flex: 1, minHeight: 0 });

export const ErrorBox = styled.div(({ theme }) => ({
  color: '#ff6b6b',
  background: theme.name === 'dark' ? '#3b1e24' : '#fef2f2',
  borderTop: `1px solid ${theme.name === 'dark' ? '#632E37' : '#fca5a5'}`,
  padding: 8,
  fontSize: 12
}));

export const AnalysisWrapper = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.codePanel.border}`,
  background: theme.name === 'dark' ? '#1a1a1a' : '#f8f9fa',
  maxHeight: 200,
  overflow: 'auto'
}));

export const AnalysisHeader = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 10px',
  gap: 6,
  borderBottom: `1px solid ${theme.codePanel.border}`,
  background: theme.codePanel.background
}));

export const AnalysisTitle = styled.span(({ theme }) => ({
  flex: 1,
  fontSize: 12,
  color: theme.codePanel.text,
  fontWeight: 500,
  letterSpacing: '.5px'
}));

export const HideAnalysisBtn = styled.button(({ theme }) => ({
  background: 'none',
  border: 'none',
  color: theme.codePanel.text,
  cursor: 'pointer',
  fontSize: 14,
  padding: 4,
  lineHeight: 1
}));

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const Spinner = styled.div(() => ({
  width: 12,
  height: 12,
  border: '2px solid transparent',
  borderTop: '2px solid #007ACC',
  borderRadius: '50%',
  animation: `${spin} 1s linear infinite`
}));

export const AnalyzingBox = styled.div(({ theme }) => ({
  fontSize: 11,
  color: theme.name === 'dark' ? '#888' : '#666',
  display: 'flex',
  alignItems: 'center',
  gap: 4
}));

export const AnalysisContent = styled.div(({ theme }) => ({
  padding: 12,
  fontSize: 13,
  lineHeight: 1.5,
  color: theme.codePanel.text
}));

export const AnalysisText = styled.div({
  whiteSpace: 'pre-wrap',
  marginBottom: 8
});

export const AnalysisFooter = styled.div(({ theme }) => ({
  fontSize: 11,
  opacity: 0.6,
  borderTop: `1px solid ${theme.codePanel.border}`,
  paddingTop: 6,
  marginTop: 6
}));

export const AnalysisError = styled.div({
  color: '#ff6b6b',
  display: 'flex',
  alignItems: 'center',
  gap: 6
});

// Панель линтера
export const LintWrapper = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.codePanel.border}`,
  background: theme.name === 'dark' ? '#141414' : '#f5f7fa',
  maxHeight: 180,
  overflow: 'auto'
}));

export const LintHeader = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '6px 10px',
  gap: 6,
  borderBottom: `1px solid ${theme.codePanel.border}`,
  background: theme.codePanel.background
}));

export const LintTitle = styled.span(({ theme }) => ({
  flex: 1,
  fontSize: 12,
  color: theme.codePanel.text,
  fontWeight: 500,
  letterSpacing: '.5px'
}));

export const LintList = styled.ul(() => ({
  listStyle: 'none',
  margin: 0,
  padding: 6,
  fontSize: 12,
  display: 'flex',
  flexDirection: 'column',
  gap: 4
}));

export const LintItem = styled.li(({ theme, severity }) => {
  const colors = {
    info: theme.name === 'dark' ? '#2b4b63' : '#e0f2fe',
    warning: theme.name === 'dark' ? '#5c4700' : '#fff4d6',
    error: theme.name === 'dark' ? '#5b1d23' : '#ffe2e2'
  };
  const borderColors = {
    info: theme.name === 'dark' ? '#356d90' : '#7dd3fc',
    warning: theme.name === 'dark' ? '#d2a100' : '#facc15',
    error: theme.name === 'dark' ? '#ff7b8a' : '#f87171'
  };
  return {
    background: colors[severity] || colors.info,
    border: `1px solid ${borderColors[severity] || borderColors.info}`,
    borderRadius: 4,
    padding: '6px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: 4
  };
});

export const LintItemHeader = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 6
});

export const LintBadge = styled.span(() => ({
  fontSize: 14,
  lineHeight: 1,
  userSelect: 'none'
}));

export const LintMeta = styled.span(() => ({
  fontSize: 11,
  opacity: 0.7
}));

export const LintActions = styled.div(() => ({
  display: 'flex',
  gap: 6,
  flexWrap: 'wrap'
}));

export const LintButton = styled.button(({ theme }) => ({
  background: theme.name === 'dark' ? '#333' : '#ffffff',
  border: `1px solid ${theme.codePanel.border}`,
  fontSize: 11,
  padding: '2px 6px',
  borderRadius: 4,
  cursor: 'pointer',
  lineHeight: 1.2,
  '&:hover': {
    background: theme.name === 'dark' ? '#444' : '#f1f5f9'
  }
}));
